import React, { useState, useEffect } from "react";
import { EditorState, convertToRaw, convertFromRaw } from "draft-js";
import { Editor as WysiwygEditor } from 'react-draft-wysiwyg';
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import "draft-js/dist/Draft.css";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

function EditEventForm({ eventId, onSuccess, onCancel }) {
  const [form, setForm] = useState({
    name: "",
    category: "",
    start_date: "",
    end_date: "",
    body: EditorState.createEmpty(),
  });
  const [thumbnail, setThumbnail] = useState(null);
  const [images, setImages] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [videos, setVideos] = useState([]);
  const [videoUrl, setVideoUrl] = useState("");
  const [existingVideos, setExistingVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    async function fetchEvent() {
      setLoading(true);
      setError("");
      try {
        const res = await fetch(`${API_URL}/api/events/${eventId}/`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token") || ""}`,
          },
        });
        if (!res.ok) throw new Error("Failed to fetch event data");
        const data = await res.json();
        const toLocalInputValue = (iso) => {
          if (!iso) return "";
          const d = new Date(iso);
          const pad = (n) => String(n).padStart(2, '0');
          const yyyy = d.getFullYear();
          const mm = pad(d.getMonth() + 1);
          const dd = pad(d.getDate());
          const hh = pad(d.getHours());
          const mi = pad(d.getMinutes());
          return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
        };
        setForm({
          name: data.name || "",
          category: data.category || "",
          start_date: toLocalInputValue(data.start_date),
          end_date: toLocalInputValue(data.end_date),
          body: data.body
            ? EditorState.createWithContent(convertFromRaw(typeof data.body === 'string' ? JSON.parse(data.body) : data.body))
            : EditorState.createEmpty(),
        });
        setExistingImages(data.images || []);
        setExistingVideos(data.videos ? data.videos.map(v => v.url) : []);
      } catch (err) {
        setError(err.message);
      }
      setLoading(false);
    }
    fetchEvent();
    // eslint-disable-next-line
  }, [eventId]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleEditorChange = (editorState) => {
    setForm({ ...form, body: editorState });
  };

  const handleThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith("image/")) {
      setThumbnail(file);
    } else {
      setError("Please select a valid image file for thumbnail.");
      setThumbnail(null);
    }
  };

  const handleImagesChange = (e) => {
    const files = Array.from(e.target.files).filter((file) => file.type.startsWith("image/"));
    if (files.length > 0) {
      setImages([...images, ...files]); // Add new images to existing ones
      setError(""); // Clear any previous error
    } else {
      setError("Please select valid image files.");
    }
  };

  const handleRemoveNewImage = (index) => {
    setImages(images.filter((_, i) => i !== index));
  };

  // Validate video URL
  const isValidVideoUrl = (url) => {
    const videoPatterns = [
      /^https?:\/\/(www\.)?(youtube\.com|youtu\.be)\/.+/,
      /^https?:\/\/(www\.)?instagram\.com\/.+/,
      /^https?:\/\/(www\.)?facebook\.com\/.+/
    ];
    return videoPatterns.some(pattern => pattern.test(url));
  };

  // Add video URL to the list
  const handleAddVideo = () => {
    if (!videoUrl.trim()) {
      setError("Please enter a video URL.");
      return;
    }
    if (!isValidVideoUrl(videoUrl)) {
      setError("Please enter a valid YouTube, Instagram, or Facebook video URL.");
      return;
    }
    setVideos([...videos, videoUrl.trim()]);
    setVideoUrl("");
    setError("");
  };

  // Remove video from the list
  const handleRemoveVideo = (index) => {
    setVideos(videos.filter((_, i) => i !== index));
  };

  // Remove existing video
  const handleRemoveExistingVideo = (index) => {
    setExistingVideos(existingVideos.filter((_, i) => i !== index));
  };

  // Track removed images
  const [removedImageIds, setRemovedImageIds] = useState([]);

  const handleRemoveExistingImage = (imgId) => {
    setExistingImages(existingImages.filter(img => img.id !== imgId));
    setRemovedImageIds([...removedImageIds, imgId]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    if (!localStorage.getItem("access_token")) {
      setError("You must be logged in to edit an event.");
      setLoading(false);
      return;
    }
    try {
      const formData = new FormData();
  formData.append("name", form.name);
  formData.append("category", form.category);
  // Convert local datetime-local values to ISO (UTC) to satisfy backend DateTimeField with timezone
  const toIso = (v) => (v ? new Date(v).toISOString() : "");
  if (form.start_date) formData.append("start_date", toIso(form.start_date));
  if (form.end_date) formData.append("end_date", toIso(form.end_date));
      if (thumbnail) formData.append("thumbnail", thumbnail);
      images.forEach((img) => formData.append("image_files", img));
      // Send IDs of images to keep and remove
      formData.append("keep_images", JSON.stringify(existingImages.map(img => img.id)));
      if (removedImageIds.length > 0) {
        formData.append("remove_images", JSON.stringify(removedImageIds));
      }
      
      // Handle video URLs - combine existing and new videos
      const allVideos = [...existingVideos, ...videos];
      if (allVideos.length > 0) {
        formData.append("video_urls", JSON.stringify(allVideos));
      }
      
      // Append body content from Draft.js editor
      const contentState = form.body.getCurrentContent();
      const body = JSON.stringify(convertToRaw(contentState));
      if (body.trim() !== "") {
        formData.append("body", body);
      } else {
        setError("Event body cannot be empty.");
        setLoading(false);
        return;
      }
      const res = await fetch(`${API_URL}/api/events/${eventId}/`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token") || ""}`,
        },
        body: formData,
      });
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(JSON.stringify(errorData) || "Failed to update event");
      }
      if (onSuccess) onSuccess();
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  return (
    <form onSubmit={handleSubmit} style={{ maxWidth: 600, margin: "auto", padding: 16 }}>
      <h2>Edit Event</h2>
      <input
        name="name"
        type="text"
        placeholder="Event Name"
        value={form.name}
        onChange={handleChange}
        required
        className="input"
      />
      <input
        name="category"
        type="text"
        placeholder="Category"
        value={form.category}
        onChange={handleChange}
        required
        className="input"
      />
      <input
        name="start_date"
        type="datetime-local"
        value={form.start_date}
        onChange={handleChange}
        required
        className="input"
      />
      <input
        name="end_date"
        type="datetime-local"
        value={form.end_date}
        onChange={handleChange}
        className="input"
      />
      <div style={{ marginTop: 8 }}>
        <label>
          Thumbnail: {" "}
          <input type="file" accept="image/*" onChange={handleThumbnailChange} />
        </label>
      </div>
      <div style={{ marginTop: 8 }}>
        <label>
          Images: {" "}
          <input type="file" accept="image/*" multiple onChange={handleImagesChange} />
        </label>
      </div>
      {existingImages.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <label>Existing Images:</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {existingImages.map(img => (
              <div key={img.id} style={{ position: 'relative' }}>
                <img src={img.url || img.image || img} alt="event" style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 4 }} />
                <button type="button" style={{ position: 'absolute', top: 0, right: 0, background: 'red', color: 'white', border: 'none', borderRadius: '50%', width: 20, height: 20, cursor: 'pointer' }} onClick={() => handleRemoveExistingImage(img.id)}>x</button>
              </div>
            ))}
          </div>
        </div>
      )}
      {images.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <label>New Images to Add:</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {images.map((img, index) => (
              <div key={index} style={{ position: 'relative' }}>
                <img src={URL.createObjectURL(img)} alt="new event" style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 4 }} />
                <button type="button" style={{ position: 'absolute', top: 0, right: 0, background: 'red', color: 'white', border: 'none', borderRadius: '50%', width: 20, height: 20, cursor: 'pointer' }} onClick={() => handleRemoveNewImage(index)}>x</button>
              </div>
            ))}
          </div>
        </div>
      )}
      <div style={{ marginTop: 8 }}>
        <label>Videos (YouTube, Instagram, Facebook URLs):</label>
        <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
          <input
            type="url"
            placeholder="Enter video URL (YouTube, Instagram, Facebook)"
            value={videoUrl}
            onChange={(e) => setVideoUrl(e.target.value)}
            style={{ flex: 1, padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
          />
          <button type="button" onClick={handleAddVideo} className="btn-primary-primary">
            Add Video
          </button>
        </div>
        {existingVideos.length > 0 && (
          <div style={{ marginTop: '8px' }}>
            <label>Existing Videos:</label>
            <div style={{ maxHeight: '120px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '4px', padding: '8px' }}>
              {existingVideos.map((videoUrl, index) => (
                <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', borderBottom: '1px solid #eee' }}>
                  <span style={{ fontSize: '14px', flex: 1, wordBreak: 'break-all' }}>
                    {videoUrl.length > 50 ? `${videoUrl.substring(0, 50)}...` : videoUrl}
                  </span>
                  <button type="button" onClick={() => handleRemoveExistingVideo(index)} style={{ background: 'red', color: 'white', border: 'none', borderRadius: '50%', width: '20px', height: '20px', cursor: 'pointer', fontSize: '12px' }}>
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        {videos.length > 0 && (
          <div style={{ marginTop: '8px' }}>
            <label>New Videos to Add:</label>
            <div style={{ maxHeight: '120px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '4px', padding: '8px' }}>
              {videos.map((videoUrl, index) => (
                <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', borderBottom: '1px solid #eee' }}>
                  <span style={{ fontSize: '14px', flex: 1, wordBreak: 'break-all' }}>
                    {videoUrl.length > 50 ? `${videoUrl.substring(0, 50)}...` : videoUrl}
                  </span>
                  <button type="button" onClick={() => handleRemoveVideo(index)} style={{ background: 'red', color: 'white', border: 'none', borderRadius: '50%', width: '20px', height: '20px', cursor: 'pointer', fontSize: '12px' }}>
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <div style={{ marginTop: 8 }}>
        <label>Body:</label>
        <div style={{ border: "1px solid #ccc", padding: 8, marginTop: 8, height: "300px", overflowY: "auto" }}>
          <WysiwygEditor
            editorState={form.body}
            onEditorStateChange={handleEditorChange}
            toolbar={{
              options: ['inline', 'blockType', 'list', 'textAlign', 'link'],
              inline: { inDropdown: true, options: ['bold', 'italic', 'underline', 'monospace'] },
              blockType: { inDropdown: true, options: ['Normal', 'H1', 'H2', 'H3', 'Blockquote'] },
              list: { inDropdown: true, options: ['unordered', 'ordered'] },
              textAlign: { inDropdown: true, options: ['left', 'center', 'right'] },
              link: { inDropdown: true },
              image: { inDropdown: true }
            }}
          />
        </div>
      </div>
      {error && <div style={{ color: "red", marginTop: 12 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
        <button
          type="submit"
          className="btn-primary-primary"
          disabled={loading}
        >
          {loading ? "Saving..." : "Save Changes"}
        </button>
        {onCancel && (
          <button type="button" className="btn-secondary" onClick={onCancel}>Cancel</button>
        )}
      </div>
    </form>
  );
}

export default EditEventForm;
